import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Minimize2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useSubscription } from '../context/SubscriptionContext';
import UpgradeModal from '../components/UpgradeModal';
import { toast } from 'sonner';
import './OpenBoxGame.css';

const OpenBoxGame = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const quizId = searchParams.get('quizId');
  const iframeRef = useRef(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showToolbar, setShowToolbar] = useState(false);
  const containerRef = useRef(null);
  const toolbarTimeoutRef = useRef(null);
  const { trackPlay, isPremium, canPlayMore, playedGamesCount, maxPlayedGames } = useSubscription();
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const playTrackedRef = useRef(false);

  const exitFullscreenAndNavigate = async () => {
    try {
      if (document.fullscreenElement) {
        await document.exitFullscreen();
      }
    } catch (error) {
      console.log('Exit fullscreen failed:', error);
    }
    navigate('/open-box-hub');
  };

  const exitFullscreen = async () => {
    try {
      if (document.fullscreenElement) {
        await document.exitFullscreen();
        setIsFullscreen(false);
      }
    } catch (error) {
      console.log('Exit fullscreen failed:', error);
    }
  };

  // Track play on mount
  useEffect(() => {
    const trackGamePlay = async () => {
      if (playTrackedRef.current) return;
      
      if (!isPremium && !canPlayMore) {
        setShowUpgradeModal(true);
        return;
      }
      
      const result = await trackPlay(`open-box-${quizId || 'new'}`, 'open-box');
      playTrackedRef.current = true;
      
      if (!result.success && result.limitReached) {
        setShowUpgradeModal(true);
      }
    };
    
    trackGamePlay();
  }, [isPremium, canPlayMore, trackPlay, quizId]);

  // Auto-enter fullscreen on mount
  useEffect(() => {
    const enterFullscreen = async () => {
      try {
        if (containerRef.current && !document.fullscreenElement) {
          await containerRef.current.requestFullscreen();
          setIsFullscreen(true);
        }
      } catch (error) {
        console.log('Fullscreen request failed:', error);
      }
    };

    // Small delay to ensure component is mounted
    const timer = setTimeout(enterFullscreen, 300);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    // If we have a quizId, load the quiz data and send it to the iframe
    if (quizId) {
      const data = localStorage.getItem('openTheBoxDatabase');
      const quizzes = data ? JSON.parse(data) : [];
      const quiz = quizzes.find(q => q.id === quizId);
      
      if (quiz && iframeRef.current) {
        // Wait for iframe to load, then send the quiz data
        const iframe = iframeRef.current;
        iframe.onload = () => {
          iframe.contentWindow.postMessage({
            type: 'loadQuiz',
            quiz: quiz
          }, '*');
        };
      }
    }

    // Listen for messages from the iframe
    const handleMessage = (event) => {
      if (event.data.type === 'SAVE_QUIZ' || event.data.type === 'saveQuiz') {
        toast.success('Quiz saved successfully!');
      } else if (event.data.type === 'EXIT_GAME') {
        exitFullscreenAndNavigate();
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [quizId, navigate]);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  // Handle toolbar visibility - show on hover at top edge
  const handleToolbarAreaHover = () => {
    setShowToolbar(true);
    
    // Clear existing timeout
    if (toolbarTimeoutRef.current) {
      clearTimeout(toolbarTimeoutRef.current);
    }
  };

  const handleToolbarMouseEnter = () => {
    if (toolbarTimeoutRef.current) {
      clearTimeout(toolbarTimeoutRef.current);
    }
    setShowToolbar(true);
  };

  const handleToolbarMouseLeave = () => {
    toolbarTimeoutRef.current = setTimeout(() => {
      setShowToolbar(false);
    }, 1000);
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (toolbarTimeoutRef.current) {
        clearTimeout(toolbarTimeoutRef.current);
      }
    };
  }, []);

  return (
    <div className="open-box-game-page" ref={containerRef}>
      {/* Small trigger area at top edge - hover to show toolbar */}
      <div 
        className="toolbar-trigger-area"
        onMouseEnter={handleToolbarAreaHover}
      />
      
      {/* Transparent auto-hide toolbar */}
      <div 
        className={`game-toolbar ${showToolbar ? 'visible' : 'hidden'}`}
        onMouseEnter={handleToolbarMouseEnter}
        onMouseLeave={handleToolbarMouseLeave}
      >
        <Button 
          onClick={exitFullscreenAndNavigate} 
          className="back-button"
          variant="ghost"
        >
          <ArrowLeft size={20} />
          Back to Hub
        </Button>
        <div className="toolbar-title">
          <span className="toolbar-icon">🎁</span>
          <span>Open the Box</span>
        </div>
        <div className="toolbar-actions">
          {isFullscreen && (
            <Button onClick={exitFullscreen} variant="ghost" className="fullscreen-btn">
              <Minimize2 size={20} />
              Exit Fullscreen
            </Button>
          )}
        </div>
      </div>
      
      <div className="game-container">
        <iframe
          ref={iframeRef}
          src="/open-the-box-standalone.html"
          title="Open the Box Game"
          className="game-iframe"
          allow="fullscreen"
        />
      </div>

      {/* Upgrade Modal */}
      <UpgradeModal 
        isOpen={showUpgradeModal}
        onClose={() => {
          setShowUpgradeModal(false);
          navigate('/open-box-hub');
        }}
        limitType="play"
        currentCount={playedGamesCount}
        maxCount={maxPlayedGames || 3}
      />
    </div>
  );
};

export default OpenBoxGame;
